import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import ImageWithBasePath from "../../../../core/img/imagewithbasebath";
import Table from "../../../../core/pagination/datatable";
import NewUser from "./NewUser";
import ResetPassword from "./ResetPassword";
import ViewUser from "./ViewUser";
import EditUser from "./EditUser";
import {
  getApiBaseUrl,
  GET_ALL_USERS,
  ADD_ACTIVITY,
} from "../../Config/Config";
import Swal from "sweetalert2";
import { Spin, Tooltip, Switch, Modal, Input, Button, Form } from "antd";

const AssetmgtUsers = () => {
  const [selectedUser, setSelectedUser] = useState([]);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingReset, setLoadingReset] = useState(false);
  const [isReauthModalVisible, setIsReauthModalVisible] = useState(false);
  const [reauthAction, setReauthAction] = useState(null);
  const [reauthForm] = Form.useForm();

  const user = JSON.parse(sessionStorage.getItem("user"));
  const currentUsername = user?.UserName || "";

  const fetchAllUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await fetch(`${getApiBaseUrl()}${GET_ALL_USERS}`);
      const userData = await response.json();
      console.log(userData.ResultData, "userData.ResultData");
      setUsers(userData.ResultData);
    } catch (err) {
      console.log(err?.message);
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    fetchAllUsers();
  }, []);

  const handleReauthentication = async (values) => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          username: currentUsername,
          password: values.password,
        }),
      });

      const loginData = await response.json();

      if (loginData.status && loginData.Message === "Login successful") {
        setIsReauthModalVisible(false);
        reauthForm.resetFields();

        if (reauthAction.type === "resetPassword") {
          await performResetPassword(reauthAction.record);
        } else if (reauthAction.type === "terminateUser") {
          await performDeleteUser(reauthAction.record);
        }
      } else {
        Swal.fire("Error", loginData?.Message, "error");
      }
    } catch (error) {
      console.error("Reauthentication error:", error);
      Swal.fire(
        "Error",
        "Reauthentication failed due to a network error.",
        "error"
      );
    }
  };

  const performDeleteUser = async (record) => {
    const user = JSON.parse(sessionStorage.getItem("user"));
    const userId = user?.UserID || 0;
    const isCurrentlyTerminated = record?.isTerminated === true;
    const action = isCurrentlyTerminated ? "Activate" : "Terminate";
    const statusFlag = isCurrentlyTerminated ? 0 : 1;

    let toggleSuccess = false;
    let notes = `User ${action.toLowerCase()} failed`;

    try {
      const response = await fetch(`${getApiBaseUrl()}/api/updateTermination`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          UserID: record?.UserID,
          isTerminated: statusFlag,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.Status && data.ResultData?.[0]?.Status === "Success") {
          toggleSuccess = true;
          notes = `User ${
            record?.Username
          } ${action.toLowerCase()}d successfully`;
          await Swal.fire(`${action}d!`, notes, "success");
        } else {
          notes = data.ResultData?.[0]?.ResultMessage || `${action} failed`;
          await Swal.fire("Error", notes, "error");
        }
      }
    } catch (error) {
      console.error(
        `Error while trying to ${action.toLowerCase()} user:`,
        error
      );
      notes = "Network error during operation";
      await Swal.fire("Error", notes, "error");
    }

    try {
      const activityPayload = {
        ActivityType: 15,
        PerformedBy: userId,
        PerformedOn: new Date().toISOString().slice(0, 19).replace("T", " "),
        Notes: notes,
        Location: "deleteuser",
        IsSucces: toggleSuccess ? 1 : 0,
      };

      await fetch(`${getApiBaseUrl()}${ADD_ACTIVITY}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(activityPayload),
      });
    } catch (error) {
      console.error("Error calling addActivity:", error);
    } finally {
      fetchAllUsers();
    }
  };

  const performResetPassword = async (record) => {
    const user = JSON.parse(sessionStorage.getItem("user"));
    const userId = user?.UserID || 0;
    setLoadingReset(true);
    try {
      const changePasswordResponse = await fetch(
        `${getApiBaseUrl()}/adminChangePassword`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            username: record?.Username,
            PerformedBy: userId,
          }),
        }
      );

      const changePasswordData = await changePasswordResponse.json();

      if (!changePasswordData.status) {
        throw new Error(
          changePasswordData.message || "Password change failed."
        );
      }

      const tempPassword = changePasswordData?.ResultData?.TemporaryPassword;

      const activityPayload = {
        ActivityType: 9,
        PerformedBy: userId,
        PerformedOn: new Date().toISOString().slice(0, 19).replace("T", " "),
        Notes: `Initiated password reset for UserID ${record?.Username}`,
        Location: "resetpassword",
        IsSucces: 1,
      };

      await fetch(`${getApiBaseUrl()}${ADD_ACTIVITY}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(activityPayload),
      });

      Swal.fire({
        title: "Temporary Password Generated",
        html: `
        <p>User <b>${record?.Name}</b> password has been reset.</p>
        <p><b>Temporary Password:</b> <code id="tempPass">${tempPassword}</code></p>
        <button id="copyBtn" class="swal2-confirm swal2-styled">Copy </button>
      `,
        showConfirmButton: false,
        didOpen: () => {
          const copyBtn = Swal.getPopup().querySelector("#copyBtn");
          copyBtn.addEventListener("click", () => {
            navigator.clipboard
              .writeText(tempPassword)
              .then(() => {
                Swal.fire(
                  "Copied!",
                  "Temporary password copied to clipboard.",
                  "success"
                );
              })
              .catch(() => {
                Swal.fire("Error", "Failed to copy to clipboard.", "error");
              });
          });
        },
      });
    } catch (error) {
      console.error("Error in resetting password:", error);
      Swal.fire("Error", error.message || "Something went wrong!", "error");
    } finally {
      setLoadingReset(false);
    }
  };

  const handleDeleteUser = async (record) => {
    const isCurrentlyTerminated = record?.isTerminated === true;
    const action = isCurrentlyTerminated ? "Activate" : "Terminate";

    const result = await Swal.fire({
      title: `${action} User ID ${record?.Username}?`,
      text: `This will ${
        isCurrentlyTerminated ? "reactivate" : "deactivate"
      } the user.`,
      icon: isCurrentlyTerminated ? "info" : "warning",
      showCancelButton: true,
      confirmButtonColor: isCurrentlyTerminated ? "#28a745" : "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: `Yes, ${action.toLowerCase()}!`,
    });

    if (!result.isConfirmed) return;

    setReauthAction({ type: "terminateUser", record });
    setIsReauthModalVisible(true);
  };

  const handleResetPassword = async (record) => {
    setReauthAction({ type: "resetPassword", record });
    setIsReauthModalVisible(true);
  };

  const columns = [
    {
      title: "User ID",
      dataIndex: "Username",
    },
    {
      title: " Name",
      dataIndex: "Name",
      render: (text, record) => (
        <span className="userimgname">
          <Link to="#" className="userslist-img bg-img">
            <ImageWithBasePath alt="" src={record.img} />
          </Link>
          <div>
            <Link
              onClick={() => setSelectedUser(record)}
              data-bs-toggle="offcanvas"
              data-bs-target="#offcanvasViewUser"
            >
              {text}
            </Link>
          </div>
        </span>
      ),
      sorter: (a, b) => a.Name.length - b.Name.length,
    },
    {
      title: "Role",
      dataIndex: "RoleName",
    },
    {
      title: "User Type",
      dataIndex: "UserType",
      render: (value) => {
        if (value === 1) {
          return <span className="badge bg-success">Process</span>;
        } else if (value === 2) {
          return <span className="badge bg-primary">Non-Process</span>;
        } else {
          return "-";
        }
      },
    },
    {
      title: "Actions",
      dataIndex: "actions",
      align: "center",
      key: "actions",
      render: (_, record) => (
        <div className="action-table-data">
          <div className="edit-delete-action">
            <Link
              className="me-2 p-2"
              to="#"
              data-bs-toggle="offcanvas"
              data-bs-target="#offcanvasEditUser"
              onClick={() => setSelectedUser(record)}
              title="Edit user"
            >
              <i data-feather="edit" className="feather-edit text-info"></i>
            </Link>
            <Link
              className="confirm-text me-2 p-2"
              title="reset password"
              to="#"
              onClick={() => handleResetPassword(record)}
            >
              <i className="fa-solid fa-key"></i>
            </Link>
            <Tooltip title={record.isTerminated ? "Inactive" : "Active"}>
              <Switch
                size="small"
                checked={!record?.isTerminated}
                onChange={() => handleDeleteUser(record)}
                style={{
                  backgroundColor: !record?.isTerminated
                    ? "#52c41a"
                    : "#ff4d4f",
                }}
              />
            </Tooltip>
          </div>
        </div>
      ),
    },
  ];

  return (
    <div>
      {loadingReset && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{
            zIndex: 1050,
            backdropFilter: "blur(4px)",
            backgroundColor: "rgba(255, 255, 255, 0.2)",
          }}
        >
          <Spin size="large" tip="Processing..." />
        </div>
      )}
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="page-title">
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <Link to="/assetmgt/dashboard">
                        <i className="fas fa-home fa-lg" />
                      </Link>
                    </li>
                    <h4 className="breadcrumb-item " aria-current="page">
                      User List
                    </h4>
                  </ol>
                </nav>
                <h6>Manage Your Users</h6>
              </div>
            </div>
            <div className="page-btn">
              <Link
                to="#"
                className="btn btn-sm btn-success"
                data-bs-toggle="offcanvas"
                data-bs-target="#offcanvasAddUser"
              >
                <i className="ti ti-circle-plus fs-4 "></i>
              </Link>
            </div>
          </div>
          <div className="card table-list-card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <div className="search-set p-3"></div>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                {loadingUsers ? (
                  <div className="text-center my-4">
                    <Spin size="large" />
                    <p>Loading data...</p>
                  </div>
                ) : users?.length > 0 ? (
                  <Table columns={columns} dataSource={users} />
                ) : (
                  <p className="text-center my-4">No data available</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <NewUser />
      <ViewUser userData={selectedUser} />
      <ResetPassword />
      <EditUser userData={selectedUser} />
      <Modal
        title="Confirm Password"
        open={isReauthModalVisible}
        onCancel={() => {
          setIsReauthModalVisible(false);
          reauthForm.resetFields();
        }}
        footer={null}
        width={350}
      >
        <Form
          form={reauthForm}
          onFinish={handleReauthentication}
          layout="vertical"
          // initialValues={{ username: currentUsername }}
        >
          <Form.Item
            name="password"
            label="Password"
            rules={[{ required: true, message: "Please enter your password" }]}
          >
            <Input.Password
              placeholder="Enter password"
              onCopy={(e) => e.preventDefault()}
              onCut={(e) => e.preventDefault()}
              onPaste={(e) => e.preventDefault()}
            />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">
              Authenticate
            </Button>
            <Button
              style={{ marginLeft: 8 }}
              onClick={() => {
                setIsReauthModalVisible(false);
                reauthForm.resetFields();
              }}
            >
              Cancel
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AssetmgtUsers;
